import { useEffect, useMemo, useState } from 'react';
import type { Anime, GuessResult } from './types';
import { POOL, dailyAnime, compare } from './game';
import { maxGuessesFor } from './routes';
import { loadProgress, saveProgress, recordWin, currentStreak, type DayProgress } from './storage';

const byId = new Map(POOL.map((a) => [a.id, a]));

export interface Game {
  answer: Anime;
  yesterday: Anime;
  guesses: Anime[];
  results: GuessResult[];
  status: DayProgress['status'];
  guessedIds: Set<number>;
  streak: number;
  max: number | null; // null = unlimited
  remaining: number | null;
  onGuess: (a: Anime) => void;
}

export function useGame(mode: string): Game {
  const max = maxGuessesFor(mode);
  const answer = useMemo(() => dailyAnime(mode), [mode]);
  const yesterday = useMemo(() => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - 1);
    return dailyAnime(mode, d);
  }, [mode]);

  const [progress, setProgress] = useState<DayProgress>(() => loadProgress(mode));
  const [streak, setStreak] = useState(() => currentStreak(mode));

  useEffect(() => {
    saveProgress(mode, progress);
    if (progress.status === 'won') setStreak(recordWin(mode).current);
    // notify the cross-mode progress display (best-practice §2.3)
    window.dispatchEvent(new Event('animedle:progress'));
  }, [mode, progress]);

  const guesses = useMemo(() => progress.guessIds.map((id) => byId.get(id)!), [progress.guessIds]);
  const results = useMemo(() => guesses.map((g) => compare(g, answer)), [guesses, answer]);
  const guessedIds = useMemo(() => new Set(progress.guessIds), [progress.guessIds]);

  function onGuess(a: Anime) {
    if (progress.status !== 'playing') return;
    const guessIds = [...progress.guessIds, a.id];
    let status: DayProgress['status'] = 'playing';
    if (a.id === answer.id) status = 'won';
    else if (max != null && guessIds.length >= max) status = 'lost';
    setProgress({ ...progress, guessIds, status });
  }

  return {
    answer, yesterday, guesses, results,
    status: progress.status, guessedIds, streak, max,
    remaining: max != null ? max - progress.guessIds.length : null,
    onGuess,
  };
}
